<script setup>
import { computed, ref } from 'vue';
import IAcquirings from '@components/IAcquirings/IAcquirings.vue';
import IPromo from '@components/IPromo/IPromo.vue';
import VueCountdown from '@chenfengyuan/vue-countdown';
import Popper from 'vue3-popper';
import useCartStore from '@vueApp/store/modules/cart';
import IOrderItem from '@components/IOrderItem/IOrderItem.vue';

const cartStore = useCartStore();
const props = defineProps({
  order: {
    type: Object,
    required: true,
  },
});

const subs = computed(() => props.order.cases.filter((caseItem) => caseItem.type === 'sub'));
const isSub = computed(() => subs.value.length > 0);

function appliedPromo(orderData) {
  cartStore.updateOrderPrices(orderData);
}

function changeLoyalty(ticket, eventId, categoryId, priceId) {
  cartStore.changeLoyalty(ticket, eventId, props.order.id, categoryId, priceId);
}

const isAnyPushkinOrCinema = computed(() => props.order.acquirings.filter(
  (acquiring) => acquiring.payType === 'Pushkin' || acquiring.payType === 'Cinema',
));

const orderTimer = ref(props.order.paymentDeadline * 1000 - Date.now());
const showCartItem = ref(orderTimer.value > 0);

function transformSingleNumberToTwoDigit(props) {
  const formattedProps = {};

  Object.entries(props).forEach(([key, value]) => {
    formattedProps[key] = value < 10 ? `0${value}` : String(value);
  });

  return formattedProps;
}

function deleteExpiredOrderById(orderId) {
  showCartItem.value = false;
  cartStore.deleteOrderByIdFromStore(orderId);
}

const isShowOrderLines = computed(
  () => props.order.sale || props.order.commission || isAnyPushkinOrCinema.value.length,
);

const isAllExtraInfoFilled = computed(() => {
  for (const event of props.order.cases) {
    const incorrect = event?.tickets?.some((ticket) => ticket.isEnableExtraInfo && !ticket.extraInfo);

    if (incorrect) {
      return false;
    }
  }

  return true;
});
</script>

<template>
  <transition name="fade">
    <div v-if="showCartItem" class="order__item">
      <div class="order__header">
        <div class="order__info">
          <div class="order__info-number">Заказ №{{ order.id }}</div>
        </div>
        <vue-countdown
          v-slot="{ minutes, seconds }"
          class="order__timer"
          :time="orderTimer"
          :transform="transformSingleNumberToTwoDigit"
          @end="deleteExpiredOrderById(order.id)"
        >
          Оплатите в течение
          <span class="order__timer_highlighted">{{ minutes }}:{{ seconds }}</span>
        </vue-countdown>
      </div>
      <div class="order__event">
        <IOrderItem
          v-for="itemCase in order.cases"
          :key="itemCase.id"
          :item-case="{ ...itemCase, orderId: order.id }"
          :trash-button="true"
          :is-history="false"
        />
      </div>
      <div v-show="!isAllExtraInfoFilled" class="order__extra-info">
        Убедитесь, что вы добавили данные о парковке
      </div>
      <div class="order__low">
        <i-promo
          v-if="!isSub"
          :order-id="order.id"
          :initial-code="order.promocode"
          class="order__promo"
          @applied="(orderData) => appliedPromo(orderData)"
        />
        <div v-show="isShowOrderLines" class="order__lines order__low-item">
          <div v-if="order.sale" class="order__line">
            <div class="order__prop">
              {{ isSub ? "Скидка за покупку абонемента" : "Скидка" }}
              <Popper
                v-if="isSub"
                class="light order__sub-info-tooltip"
                placement="top"
                arrow
                hover
                content="Размер скидки зависит от количества выбранных Вами мероприятий."
              >
                <svg width="16" height="16" fill="none">
                  <use xlink:href="#question" />
                </svg>
              </Popper>
            </div>
            <div class="order__value">{{ order.sale }} ₽</div>
          </div>

          <div v-if="order.commission" class="order__line">
            <div class="order__prop">Комиссия</div>
            <div class="order__value">{{ order.commission }} ₽</div>
          </div>
        </div>
        <i-acquirings :summary-price="order.summaryPrice" :order="order" />
      </div>
    </div>
  </transition>
</template>

<style scoped lang="scss">
@import "frontend/web/src/styles/utils/variables.scss";

:deep(.popper) {
  width: 280px;
  padding: 5px;

  border-radius: 6px;
  box-shadow: 0px 5px 5px rgba(0, 0, 0, 0.05);
  background-color: $white;

  color: #000;
  text-align: center;
  font-size: 14px;
  font-weight: 400;
}

:deep(.popper #arrow::before) {
  transform: translateX(-5px) rotate(45deg);
  background: $white;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.tickets-item {
  margin-right: 16px;
  margin-bottom: 8px;
  display: flex;
  flex-direction: column;
}
@media (max-width: $screen-small) {
  .tickets-item {
    margin-right: 0px;
    width: 100%;
  }
}
</style>
